import { groupBy, isEmpty, keyBy, mapValues, partition, set } from 'lodash/fp';

import type {
  CombinedStep,
  CombinedStepForTree,
  LinkedWorkflowStep,
  PreviewPageMap,
  StepPreviewMap,
  TemplateStepPreview,
  TreeNode,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { WorkflowVersionMap } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import {
  firstInitialStep,
  uiStepAllComponentConfigs,
  uiStepWorkflowVersionTargets,
} from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import {
  isUpdateInquiryNextStepActionStep,
  workflowVtmplvIds,
} from 'dashboard/apps/InquiryTemplateEditor/utils/workflow-steps';
import { VERIFICATION_TEMPLATE_VERSION_TYPE_NAMES_SHORT } from 'dashboard/lib/constants/verification';
import { errorFieldMatchesConditionalBranchIndex } from 'dashboard/lib/workflows/validation';
import {
  InquiryTemplateVersionStep,
  isUiStepConfig,
} from 'dashboard/models/inquiry-template-version';
import { VerificationTemplateVersion } from 'dashboard/models/verification-template-version';
import {
  WorkflowConfigError,
  WorkflowFieldObjectKey,
  WorkflowSchema,
} from 'dashboard/models/workflow';
import {
  WorkflowStep,
  WorkflowStepActionKey,
  WorkflowStepConditional,
  WorkflowStepType,
} from 'dashboard/models/workflow-version';
import { FieldSchema } from 'lib/fields';
import { UiStepType } from 'lib/interfaces/StepsConfig';
import {
  isUiParentComponentConfig,
  isUiTransitionComponentConfig,
  UiComponentConfig,
  UiComponentConfigType,
} from 'lib/interfaces/ui-components';
import { assertNever } from 'lib/utils/types';

export const PLACEHOLDER_STEP_NAME = 'placeholder_step_name';

/**
 * Returns all ITV steps which directly (combined steps, via config)
 * or indirectly (ui + combined steps, via referenced workflow versions)
 * reference a given verification template version
 */
export const getVtmplvStepReferences = (
  vtmplvId: string,
  steps: InquiryTemplateVersionStep[],
  workflowVersionMap: WorkflowVersionMap,
): InquiryTemplateVersionStep[] => {
  const wfvReferenceIds = Object.keys(workflowVersionMap).filter((wfvId) =>
    workflowVtmplvIds(workflowVersionMap[wfvId].attributes['steps-config']).includes(vtmplvId),
  );
  return steps.filter((step) => {
    switch (step.type) {
      case UiStepType.Complete:
      case UiStepType.Integration:
      case UiStepType.VerifyWithPersona:
      case UiStepType.Loop:
        return false;
      case UiStepType.Document:
      case UiStepType.GovernmentId:
      case UiStepType.Selfie:
      case UiStepType.SelfieReflection: {
        const {
          'verification-template-version-id': stepVtmplvId,
          'workflow-version-id': workflowVersionId,
        } = step.config;
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        return stepVtmplvId === vtmplvId || wfvReferenceIds.includes(workflowVersionId);
      }
      case UiStepType.Ui:
        return uiStepWorkflowVersionTargets(step).some((stepWfvId) =>
          wfvReferenceIds.includes(stepWfvId),
        );
      default:
        assertNever(step);
    }
  });
};

/**
 * Standardizes vtmplv names displayed in FLE.
 * Fallback order is parent template name, version name, type + (token).
 * Current usages are FLE config editor + internal diff modal, and the
 * standalone Vtmplv pages.
 */
export const getVtmplvDisplayName = (
  vtmplVersion: VerificationTemplateVersion | undefined,
): string => {
  if (vtmplVersion == null) return '';
  const { name, 'template-name': templateName } = vtmplVersion.attributes;
  return (
    templateName ||
    name ||
    getVtmplvOptionLabel({ type: vtmplVersion.type, token: vtmplVersion.id })
  );
};

/**
 * Standardizes vtmplv names displayed in
 * FLE option lists to be {Vtmplv type} (token).
 * Current usage is the workflow editor action selector.
 *
 * TODO (eric): improve unique identifier, extend
 * usage to permissions revamp dropdowns
 */
export const getVtmplvOptionLabel = ({
  type,
  token,
  name,
}: {
  type: VerificationTemplateVersion['type'];
  token: string;
  name?: string;
}): string => {
  const typeLabel = name || VERIFICATION_TEMPLATE_VERSION_TYPE_NAMES_SHORT[type];
  return `${typeLabel} (${token})`;
};

/**
 * Given a flattened list of un-nested components, returns original component array
 * with any updates applied to the nested children
 *
 * We iterate through the component list in reverse order to ensure that nested children
 * are updated properly before being 'passed' to their parent comonent.
 *
 * The child component names from the flattened component array are tracked and filtered
 * out of the updated list before being returned.
 */
export const refoldComponents = (components: UiComponentConfig[]): UiComponentConfig[] => {
  const updatedComponents: UiComponentConfig[] = [];
  const childComponentNames: string[] = [];
  [...components].reverse().forEach((component) => {
    if (isUiParentComponentConfig(component)) {
      const updatedChildren = component.config.children
        .map((child) => {
          const updatedChildComponent = updatedComponents.find(
            (component) => component.name === child.name,
          );
          if (updatedChildComponent?.name != null) {
            childComponentNames.push(updatedChildComponent.name);
            return updatedChildComponent;
          }
        })
        .filter(Boolean);
      updatedComponents.unshift(set(['config', 'children'], updatedChildren, component));
    } else {
      updatedComponents.unshift(component);
    }
  });
  return updatedComponents.filter((component) => !childComponentNames.includes(component.name));
};

/**
 * Reorder workflow steps to ensure that the UpdateInquiryNextStepName is last
 * as any other steps will not be rendered if they come after due to
 * nextStep being set to a screen step
 */
export const getReorderedSteps = (branch: WorkflowStep[]): WorkflowStep[] => {
  const routingStep = branch.find(
    (branchStep) =>
      branchStep?.type === WorkflowStepType.Action &&
      branchStep?.action.key === WorkflowStepActionKey.UpdateInquiryNextStepName,
  );
  return (
    branch
      .filter((branchStep) => branchStep.token !== routingStep?.token)
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      .concat(routingStep)
      .filter(Boolean)
  );
};

/**
 *
 * @param stepKey string - key name used to when generating pages from
 *  dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/pages
 * @param stepName string - name of combined step
 * @returns string - screen name of preview page in combined step
 */
export const deriveCombinedStepScreenName = (stepKey: string, stepName: string) => {
  // screen names should only consist of just [xxx]-page, so remove characters beyond that
  return stepKey.replace(`${stepName}-`, '').replace(/(-(?!page).*)*$/i, '');
};

export const mapStepsPreviewAndToken = ({
  steps,
  stepsPreviewMap,
  previewPageMap,
}: {
  steps: InquiryTemplateVersionStep[];
  stepsPreviewMap: StepPreviewMap;
  previewPageMap: PreviewPageMap;
}): TemplateStepPreview[] => {
  return steps
    .filter((step) => previewPageMap[step.name])
    .map((step) => {
      switch (step.type) {
        case UiStepType.Complete:
          return {
            ...step,
            token: step.name,
          };
        case UiStepType.Document:
        case UiStepType.GovernmentId:
        case UiStepType.Selfie:
        case UiStepType.SelfieReflection:
        case UiStepType.VerifyWithPersona: {
          const stepPreview = stepsPreviewMap[step.name];
          const [page, ...combinedStepPages] = previewPageMap[step.name];
          return {
            ...step,
            stepPreview,
            page,
            combinedStepPages,
            token: step.name,
          };
        }
        case UiStepType.Integration:
        case UiStepType.Ui:
        case UiStepType.Loop:
          return {
            ...step,
            stepPreview: stepsPreviewMap[step.name],
            page: previewPageMap[step.name]?.[0],
            token: step.name,
          };
        default:
          assertNever(step);
      }
    });
};

/**
 * Builds a map of steps with parent and child relations.
 */
export const buildGraph = (
  stepsToTraverse: CombinedStep[],
  initialStepName: string,
  options: { showVerifyWithPersona?: boolean },
): Record<string, TreeNode> => {
  const stepMap: Record<string, CombinedStep> = keyBy('token', stepsToTraverse);

  // Generate edges for establishing step hierarchy
  const { edges } = traverse(
    stepMap,
    options,
  )({
    visitedMap: mapValues(() => false, stepMap),
    stepsToTraverse,
    edges: [],
  });

  const tokensToNode = stepsToTraverse.reduce<Record<string, TreeNode>>((map, step) => {
    map[step.token] = {
      step,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      parent: null,
      retryParents: [],
      children: [],
    };
    return map;
  }, {});

  const sortedEdges = sortEdges(edges, initialStepName);
  // Add parent and child relationships via edges
  sortedEdges.forEach(({ target, source }) => {
    if (target === PLACEHOLDER_STEP_NAME) {
      tokensToNode[source].children.push(target);
    }
    // skip if target or source not found in tokensToNode
    if (!tokensToNode[target] || !tokensToNode[source]) {
      return;
    }
    tokensToNode[target].parent = source;
    tokensToNode[source].children.push(target);
  });
  return tokensToNode;
};

/**
 * Given a list of edges, topologically sorts them while using initial source
 * as the start edge.
 */
export function sortEdges(edges: Edge[], initialSource: string): Edge[] {
  const visited = new Set<Edge>();
  const stack: Edge[] = [];
  const edgeMap = groupBy((edge) => edge.source, edges);

  /**
   * Recursive topological sort helper
   */
  const topoSortHelper = (edge: Edge, visited: Set<Edge>, stack: Edge[]) => {
    visited.add(edge);
    stack.push(edge);

    // will be empty if target edge is a terminal edge
    if (edgeMap[edge.target] != null) {
      edgeMap[edge.target].forEach((targetEdge) => {
        if (!visited.has(targetEdge)) {
          topoSortHelper(targetEdge, visited, stack);
        }
      });
    }
  };

  // There may be more than one edge where source is the initial step
  const [initialEdges, restEdges] = partition((edge) => edge.source === initialSource, edges);

  // Place initial edges at start to ensure toposort has right origin
  initialEdges.concat(restEdges).forEach((edge) => {
    if (!visited.has(edge)) {
      topoSortHelper(edge, visited, stack);
    }
  });

  return stack;
}

export interface Edge {
  source: string;
  target: string;
}

export interface TraverseArgs {
  visitedMap: Record<string, boolean>;
  stepsToTraverse: CombinedStep[];
  edges: Edge[];
}

/**
 * Does a breadth-first traversal of the graph to build nodes and edges.
 */
export const traverse = (
  stepMap: Record<string, CombinedStep>,
  options: { showVerifyWithPersona?: boolean } = {},
) => {
  const traverseRecursive = ({
    visitedMap,
    stepsToTraverse,
    edges,
  }: TraverseArgs): TraverseArgs => {
    // Base case: return from recursion
    if (stepsToTraverse.length === 0) {
      return {
        visitedMap,
        stepsToTraverse,
        edges,
      };
    }

    const [curr, ...restSteps] = stepsToTraverse;

    // This graph may be cyclic. Ignore revisited steps.
    if (visitedMap[curr.token]) {
      return traverseRecursive({
        visitedMap,
        stepsToTraverse: restSteps,
        edges,
      });
    }

    const newEdges: Edge[] = buildNewEdges(curr, options);

    // Check for edges linked to nodes that do not exist
    // This implies that a step was removed without updating it s parent
    const [_orphanEdges, existingNewEdges] = partition(
      (edge) => edge.target !== PLACEHOLDER_STEP_NAME && stepMap[edge.target] == null,
      newEdges,
    );
    // Forbid self cycles
    const [_selfCycleEdges, validNewEdges] = partition(
      (edge) => edge.target === edge.source,
      existingNewEdges,
    );

    const newStepsToTraverse = validNewEdges
      .filter((edge) => edge.target !== PLACEHOLDER_STEP_NAME)
      .map((edge) => stepMap[edge.target]);

    return traverseRecursive({
      visitedMap: set(curr.token, true, visitedMap),
      stepsToTraverse: restSteps.concat(newStepsToTraverse),
      edges: edges.concat(newEdges),
    });
  };

  return traverseRecursive;
};

const buildNewEdges = (
  step: CombinedStep,
  options: { showVerifyWithPersona?: boolean; loopStepToken?: string } = {},
): Edge[] => {
  const newEdges: Edge[] = [];
  switch (step.type) {
    case UiStepType.Complete:
      // complete is a non-UI terminal step that will never transition to another
      break;
    case UiStepType.Ui: {
      // For every component that can trigger a transition, add an edge.
      uiStepAllComponentConfigs(step)
        .filter(isUiTransitionComponentConfig)
        .filter(
          (component) =>
            component.type !== UiComponentConfigType.ButtonVerifyWithPersona ||
            options?.showVerifyWithPersona,
        )
        .forEach((component) => {
          if (component.config['to-step']) {
            newEdges.push({
              source: options?.loopStepToken ? options?.loopStepToken : step.token,
              target: component.config['to-step'],
            });
            // should always be mutually exclusive
          } else if (component.config['workflow-version-id']) {
            if (!options?.loopStepToken) {
              newEdges.push({
                source: step.token,
                target: component.config['workflow-version-id'],
              });
            }
          } else {
            newEdges.push({
              source: step.token,
              target: PLACEHOLDER_STEP_NAME,
            });
          }
        });
      break;
    }
    case UiStepType.Document:
    case UiStepType.Integration:
    case UiStepType.VerifyWithPersona:
    case UiStepType.Selfie:
    case UiStepType.SelfieReflection:
    case UiStepType.GovernmentId: {
      if (step.config['to-step']) {
        newEdges.push({
          source: options?.loopStepToken ? options?.loopStepToken : step.token,
          target: step.config['to-step'],
        });
        // should always be mutually exclusive
      } else if (step.config['workflow-version-id']) {
        if (!options?.loopStepToken) {
          newEdges.push({
            source: step.token,
            target: step.config['workflow-version-id'],
          });
        }
      }
      break;
    }
    case 'workflow': {
      newEdges.push(
        ...step.nextSteps.filter(Boolean).map((nextStep) => ({
          source: step.token,
          target: nextStep,
        })),
      );
      break;
    }
    case UiStepType.Loop: {
      const newLoopEdges = step.config.steps
        .map((loopStep) => buildNewEdges(loopStep, { loopStepToken: step.token }))
        .flat()
        .map((edge) => ({
          ...edge,
          source: step.token,
        }));
      newEdges.push(...newLoopEdges);
      break;
    }
    default:
      assertNever(step);
  }
  // Remove duplicates
  return newEdges.filter(
    (edge, index, self) =>
      index === self.findIndex((e) => e.source === edge.source && e.target === edge.target),
  );
};

const buildTree = (
  step: CombinedStep,
  graph: Record<string, TreeNode>,
): [CombinedStepForTree[], Set<string>] => {
  const visitedSet = new Set<string>();
  return [buildTreeRecursive(visitedSet, step, graph), visitedSet];
};

/**
 * Builds tree from ITV/workflow steps which visually represents the Inquiry flow.
 * Notes:
 * 1. uses visited Set which is mutated to ensure visited steps return only themselves
 *    upon subsequent visits rather than duplicating the subtree.
 *    (needed for rendering connectors, which are visual representations of step pointers)
 * 2. omits update_next_step_actions as these are now implicitly represented by branching node connectors
 * 3. omits workflow_on_error_step_name as these are not rendered within the FLE tree due to users being confused on this branching logic
 * 4. adds support for branches originating from non-workflow steps
 *    (screens with multiple buttons or workflow_on_error_step_name)
 *
 * @returns combined screen + workflow steps used to render ScreenFlow in ITE flow editor
 */
const buildTreeRecursive = (
  visited: Set<string>,
  step: CombinedStep,
  graph: Record<string, TreeNode>,
): CombinedStepForTree[] => {
  // Can be null if attempting to recurse the empty branch placeholder, as there
  // is no step for this empty branch
  if (step == null) return [];

  /**
   * return just the current step if step has already been visited.
   * avoids duplicate subtrees, instead renders a reference to an existing screen (connector)
   */
  if (visited.has(step.token)) {
    return [
      {
        ...step,
        isConnector: true,
      },
    ];
  }

  visited.add(step.token);
  const hasBranches = graph[step.token].children.length > 1;

  /**
   * UiStepType.Complete is a non-UI terminal step. For all other step types, return:
   * - step + recursive remainder of branch OR
   * - step with branches (+recursive branch paths)
   */
  switch (step.type) {
    case UiStepType.Complete:
      return [step];
    case UiStepType.Loop:
      return [
        step,
        ...graph[step.token].children.flatMap((node) =>
          buildTreeRecursive(visited, graph[node]?.step, graph),
        ),
      ];
    case UiStepType.Document:
    case UiStepType.Integration:
    case UiStepType.VerifyWithPersona:
    case UiStepType.GovernmentId:
    case UiStepType.Selfie:
    case UiStepType.SelfieReflection:
    case UiStepType.Ui: {
      /**
       * branches for UI steps occur when:
       * - the step has multiple buttons OR
       * - a button on the step transitions to a workflow, as the
       *   workflow_on_error_step_name attribute is then required
       */
      return hasBranches
        ? [
            {
              ...step,
              branches: graph[step.token].children.map((node) =>
                buildTreeRecursive(visited, graph[node]?.step, graph),
              ),
            },
          ]
        : [
            step,
            ...graph[step.token].children.flatMap((node) =>
              buildTreeRecursive(visited, graph[node]?.step, graph),
            ),
          ];
    }
    case 'workflow': {
      /**
       * workflowStepType is added to the node for stronger typing in ScreenFlow
       * update_next_step actions are omitted, as these aren't rendered in ScreenFlow.
       */
      const currentStep = {
        ...step,
        workflowStepType: step.workflowStep?.type,
      };
      const skipCurrentStep = step.workflowStep.isUpdateNextStepAction;
      return step.workflowStep?.type === WorkflowStepType.Conditional
        ? [
            {
              ...currentStep,
              // map with step.nextSteps over graph[step.token].children to support empty branches
              branches: step.nextSteps.map(
                (nextStepToken) =>
                  nextStepToken != null
                    ? buildTreeRecursive(visited, graph[nextStepToken].step, graph)
                    : [], // still want to display a branch even if its empty
              ),
            },
          ]
        : [
            ...(skipCurrentStep ? [] : [currentStep]),
            ...graph[step.token].children.flatMap((node) =>
              buildTreeRecursive(visited, graph[node].step, graph),
            ),
          ];
    }
    default:
      assertNever(step);
  }
};

// The following function is a simplified version of the buildTree logic
// with two minor differences:
// - Workflow on error step names are accounted for
// - Only the visited set is calculated
// Used when deleting workflows to prune all disconnected steps from the tree.
export const buildConnectedSteps = ({
  steps,
  stepsPreviewMap,
  previewPageMap,
  extractedWorkflowSteps,
  isITEInternal,
}: BuildTreeAndGraphParams): Set<string> => {
  const initialStep = firstInitialStep(steps);
  if (initialStep == null) {
    return new Set();
  }
  const combinedSteps = [
    ...mapStepsPreviewAndToken({ steps, stepsPreviewMap, previewPageMap }),
    ...extractedWorkflowSteps,
  ];
  const treeRootStep = combinedSteps.find((step) => step.token === initialStep.name);

  const visitedSet = new Set<string>();

  if (treeRootStep) {
    const graph = buildGraph(combinedSteps, initialStep.name, {
      showVerifyWithPersona: isITEInternal,
    });
    buildConnectedStepsRecursive(visitedSet, treeRootStep.token, graph);
  }
  return visitedSet;
};

const buildConnectedStepsRecursive = (
  visited: Set<string>,
  stepToken: string,
  graph: Record<string, TreeNode>,
) => {
  const node = graph[stepToken];
  // Node will be null if the stepToken is a placeholder
  if (node == null) return;

  const step = node.step;
  if (visited.has(step.token)) return;

  visited.add(step.token);
  const hasBranches = graph[step.token].children.length > 1;

  switch (step.type) {
    case UiStepType.Complete:
      break;
    case UiStepType.Loop: {
      const loopSteps = mapStepsPreviewAndToken({
        steps: step.config.steps,
        stepsPreviewMap: {},
        previewPageMap: {},
      });
      loopSteps.map((step) => {
        buildConnectedStepsRecursive(visited, step.token, graph);
      });
      break;
    }
    case UiStepType.Document:
    case UiStepType.Integration:
    case UiStepType.VerifyWithPersona:
    case UiStepType.GovernmentId:
    case UiStepType.Selfie:
    case UiStepType.SelfieReflection: {
      if (step.config['workflow-on-error-step-name'])
        visited.add(step.config['workflow-on-error-step-name']);
      // fallthrough
    }
    case UiStepType.Ui: {
      if (isUiStepConfig(step)) {
        uiStepAllComponentConfigs(step)
          .filter(isUiTransitionComponentConfig)
          .forEach((component) => {
            if (component.config['workflow-on-error-step-name']) {
              visited.add(component.config['workflow-on-error-step-name']);
            }
          });
      }

      if (hasBranches) {
        graph[step.token].children.map((node) =>
          buildConnectedStepsRecursive(visited, node, graph),
        );
      } else {
        graph[step.token].children.flatMap((node) =>
          buildConnectedStepsRecursive(visited, node, graph),
        );
      }
      break;
    }
    case 'workflow': {
      if (step.workflowStep?.type === WorkflowStepType.Conditional) {
        step.nextSteps.map((nextStepToken) => {
          if (nextStepToken != null) {
            buildConnectedStepsRecursive(visited, nextStepToken, graph);
          }
        });
      } else {
        graph[step.token].children.flatMap((node) =>
          buildConnectedStepsRecursive(visited, node, graph),
        );
      }
      break;
    }
    default:
      assertNever(step);
  }
};

/**
 * Returns token representing a specific branch of a conditional
 */
export const getBranchToken = (branchSteps: WorkflowStep[], workflowVersionId: string) => {
  if (branchSteps.length === 0) {
    return null;
  }
  // append wfv prefix in front of branch token to disambiguate steps with the same token but from different workflow versions
  // this can arise when wfvs are merged due to deletion of a screen step that was in the middle
  return `${workflowVersionId}.${getReorderedSteps(branchSteps)[0].token}`;
};

/**
 * Extracts two variants of visual logic representation in ScreenFlow from workflow versions in template.
 * 1. Actions node: linear sequence of (typically action) workflow steps
 * 2. Conditional node: conditional step + branches
 *
 * @returns list of linear action steps or conditional branching used to build template graph
 */
export const extractNextStepsAndConditionals = (
  steps: WorkflowStep[],
  workflowVersionId: string,
): LinkedWorkflowStep[] => {
  // Workflow can have empty steps if last node was deleted from a wfv
  if (!steps.length) {
    return [];
  }

  // Reorder steps for the base case so update inquiry next step action is last.
  // This prevents skipCurrentStep from being true and omitting the rest of the actions inside of buildTree
  steps = getReorderedSteps(steps);

  const firstStep = steps[0];

  // Every workflow renders in the ITV flow tree with some/none/all of the following components:
  // 1. An action sequence node, if (non-next-step) actions exist in the WF. Clicking this opens the WFE
  // 2. A conditional node, if a conditional exists that routes to a ITV step
  // 3. Conditional branches, if a conditional node exists
  // 4. A connector that directly connects two ITV steps, if routing to next step is done directly and
  //    not through a conditional

  let workflowStep;
  if (firstStep.type !== WorkflowStepType.Conditional) {
    // We pick the first workflow step to render as the action sequence node as long as it's not a conditional
    workflowStep = firstStep;
  } else {
    // Otherwise we find the first action step within the conditional to renders as the action sequence node
    const firstActionStep = firstStep.branches
      .flatMap((branch) => branch)
      .find(
        (node) => node.type === WorkflowStepType.Action && !isUpdateInquiryNextStepActionStep(node),
      );
    // Fallback to `firstStep` if we cannot find a non-next-step-action within the conditional.
    // Generally should not happen but falling back to ensure that the flow does not crash,
    // though the rendering would still be messed up
    workflowStep = firstActionStep ?? firstStep;
  }

  const currentNode: LinkedWorkflowStep = {
    type: 'workflow',
    // append wfv prefix in front of step token to disambiguate steps with the same token but from different workflow versions
    // this can arise when wfvs are merged due to deletion of a screen step that was in the middle
    token: `${workflowVersionId}.${workflowStep.token}`,
    workflowVersionId,
    workflowStep: {
      token: workflowStep.token,
      type: workflowStep.type,
      name: workflowStep.name,
      isUpdateNextStepAction: isUpdateInquiryNextStepActionStep(workflowStep),
    },
    nextSteps: [],
  };

  const conditionalSteps = steps.filter(
    (step): step is WorkflowStepConditional => step.type === WorkflowStepType.Conditional,
  );

  // Pick the most appropriate branch step to render, if available. That is,
  // prioritize a conditional that has a next step action in its branches. If none exists
  // choose the first available conditional
  let branchStep;
  if (conditionalSteps.length >= 1) {
    branchStep =
      conditionalSteps.find((step) =>
        step.branches.some((branches) =>
          branches.some((step) => isUpdateInquiryNextStepActionStep(step)),
        ),
      ) ?? conditionalSteps[0];
  }

  if (branchStep == null) {
    const updateStepAction = steps.find(isUpdateInquiryNextStepActionStep)?.action;
    currentNode.nextSteps =
      updateStepAction?.params['next-step-name'] != null
        ? [updateStepAction.params['next-step-name']]
        : [];
    return [currentNode];
  } else {
    const branchStepNode: LinkedWorkflowStep = {
      type: 'workflow',
      token: `${workflowVersionId}.${branchStep.token}`,
      workflowVersionId,
      workflowStep: {
        token: branchStep.token,
        type: WorkflowStepType.Conditional,
        name: branchStep.name,
      },
      // purposefully leave null token values for empty branches, as they're used in buildTreeRecursive to render empty branches
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      nextSteps: branchStep.branches.map((branch) => getBranchToken(branch, workflowVersionId)),
    };
    // no longer need to reorder branch steps here because we do it at beginning of extractNextStepsAndConditionals
    const branchNodes: LinkedWorkflowStep[] = branchStep.branches
      .filter((branch) => branch.length)
      .flatMap((branch) => extractNextStepsAndConditionals(branch, workflowVersionId));
    // omit duplicate initial node when initial workflow step is conditional
    if (branchStep.token === steps[0].token) {
      return [branchStepNode, ...branchNodes];
    } else {
      return [
        {
          ...currentNode,
          nextSteps: [`${workflowVersionId}.${branchStep.token}`],
        },
        branchStepNode,
        ...branchNodes,
      ];
    }
  }
};

export const extractActionsAndConditionalNodes = (
  workflowVersionMap: WorkflowVersionMap,
): LinkedWorkflowStep[] => {
  return Object.values(workflowVersionMap).flatMap((workflowVersion) => {
    const allWorkflowSteps: LinkedWorkflowStep[] = extractNextStepsAndConditionals(
      workflowVersion.attributes['steps-config'],
      workflowVersion.id,
    );

    // Workflow can have empty steps if last node was deleted from a wfv
    if (allWorkflowSteps.length === 0) {
      return [];
    }

    // update first step token to workflowVersion token before returning
    return [
      {
        ...allWorkflowSteps[0],
        token: workflowVersion.id,
      },
    ].concat(allWorkflowSteps.slice(1));
  });
};

/**
 * @param steps server representation of ITV steps data
 * @param stepsPreview client data representation of ITV steps
 * @param pages preview component to render for each step in tree, props populated via stepsPreview
 * @param extractedWorkflowSteps action seqeuence + branch node workflow steps
 */
type BuildTreeAndGraphParams = {
  steps: InquiryTemplateVersionStep[];
  stepsPreviewMap: StepPreviewMap;
  previewPageMap: PreviewPageMap;
  extractedWorkflowSteps: LinkedWorkflowStep[];
  isITEInternal: boolean;
};

export const buildScreenTreeAndGraph = ({
  steps,
  stepsPreviewMap,
  previewPageMap,
  extractedWorkflowSteps,
  isITEInternal,
}: BuildTreeAndGraphParams): [CombinedStepForTree[], Record<string, TreeNode>, Set<string>] => {
  const initialStep = firstInitialStep(steps);
  if (initialStep == null) {
    return [[], {}, new Set()];
  }
  const combinedSteps = [
    ...mapStepsPreviewAndToken({ steps, stepsPreviewMap, previewPageMap }),
    ...extractedWorkflowSteps,
  ];
  const treeRootStep = combinedSteps.find((step) => step.token === initialStep.name);
  const graph = buildGraph(combinedSteps, initialStep.name, {
    showVerifyWithPersona: isITEInternal,
  });
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [tree, visited] = buildTree(treeRootStep, graph);
  return [tree, graph, visited];
};

export const updateWorkflowSchemaInquiryFields = (
  workflowSchema: WorkflowSchema,
  inquiryFieldSchemas: FieldSchema[],
  inquiryTemplateToken?: string,
): WorkflowSchema => {
  const inquiryTemplateFieldConfig = {
    fields: {
      key: 'fields',
      'default-value': null,
      label: null,
      type: 'hash',
      config: {
        'item-schemas': inquiryFieldSchemas.filter((schema) => !isEmpty(schema)),
      },
    },
  };

  return inquiryTemplateToken
    ? set(
        ['criteria-variable-schemas', WorkflowFieldObjectKey.Inquiry, 'with-template'],
        { [inquiryTemplateToken.replace(/_/g, '-')]: inquiryTemplateFieldConfig },
        workflowSchema,
      )
    : workflowSchema;
};

export const validationErrorsForWorkflowNode = (
  treeStep: LinkedWorkflowStep,
  workflowSteps: WorkflowStep[],
  validationErrors: WorkflowConfigError[],
  branchIndex?: number,
): WorkflowConfigError[] => {
  const targetStep = treeStep.workflowStep;
  if (targetStep.type === WorkflowStepType.Conditional) {
    if (branchIndex == null) {
      return validationErrors.filter((error) => error.token === targetStep.token);
    } else {
      return validationErrors.filter((error) => {
        return (
          error.token === targetStep.token &&
          errorFieldMatchesConditionalBranchIndex(error.field, branchIndex)
        );
      });
    }
  } else {
    if (workflowSteps.find((step) => step.token === targetStep.token)) {
      const matchingWorkflowSteps = workflowSteps.filter(
        (step) => step.type !== WorkflowStepType.Conditional,
      );

      return validationErrors.filter(
        (error) => !!matchingWorkflowSteps.find((step) => step.token === error.token),
      );
    } else {
      return workflowSteps.flatMap((step) => {
        if (step.type === WorkflowStepType.Conditional) {
          return step.branches.flatMap((branchSteps) =>
            validationErrorsForWorkflowNode(treeStep, branchSteps, validationErrors),
          );
        } else {
          return [];
        }
      });
    }
  }
};

/**
 * @param workflowNodes extracted action sequence or branch nodes in tree view
 * @param workflowVersionMap map of workflow versions in ITV
 * @param validationErrorMap map of workflow errors
 * @returns first workflow node in tree with errors to highlight or pan to
 */
export const getFirstWorkflowValidationErrorNode = (
  workflowNodes: LinkedWorkflowStep[],
  workflowVersionMap: WorkflowVersionMap,
  validationErrorMap: Record<string, WorkflowConfigError[]>,
): LinkedWorkflowStep | null => {
  for (const node of workflowNodes) {
    const errors = validationErrorsForWorkflowNode(
      node,
      workflowVersionMap[node.workflowVersionId].attributes['steps-config'],
      validationErrorMap[node.workflowVersionId] ?? [],
    );
    if (errors.length > 0) {
      return node;
    }
  }
  return null;
};
